import ConfirmationModal from "components/common/ConfirmationModal";
import { createForm } from "modules/form";
import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { connect } from "react-redux";
import {
  editHostPairingKeyModal,
  EDIT_HOST_PAIRING_KEY,
  edgeMachineDetailsFetcher,
} from "state/cluster/services/edgemachines";
import Fields from "components/ui/Fields";
import {
  editPairingKeyActions,
  onRemoteShellToggle,
} from "state/cluster/actions/list/edgemachines";
import DocLink from "components/common/DocLink";
import permissions from "services/permissions";
import { coerce, gte } from "semver";
import Alert from "components/common/Alert";
import styled from "styled-components";
import { inputBorder } from "utils/constants/colors";

const MIN_STYLUS_VERSION = "4.6.6";

const StyledAlert = styled(Alert)`
  padding: 8px 16px;
  align-items: center;
`;
const StyledWrapper = styled.div`
  border: 1px solid ${inputBorder};
  padding: 12px 16px;
`;

export function isRemoteShellAllowed(agentVersion = "") {
  const cleanVersion = coerce(agentVersion)?.version;
  if (!cleanVersion) {
    return;
  }

  return gte(cleanVersion, MIN_STYLUS_VERSION);
}

const EditPairingKeyForm = createForm({
  actions: editPairingKeyActions,
  Component: connect(
    (state) => ({
      remoteSsh: state.forms[EDIT_HOST_PAIRING_KEY]?.data?.remoteSsh,
      edgeHost: edgeMachineDetailsFetcher?.selector(state)?.result,
    }),
    {
      onRemoteShellToggle,
    }
  )(function Form({ remoteSsh, onRemoteShellToggle, edgeHost = {} }) {
    const { t } = useTranslation();
    const agentVersion = edgeHost?.status?.health?.agentVersion;
    const canUpdateRemoteShell =
      edgeHost?.spec?.host?.remoteSsh?.toLowerCase() === "enabled";

    function renderTemporaryAuthSwitch() {
      if (!remoteSsh) {
        return;
      }

      return (
        <Fields.Switch
          name="remoteSshTempUser"
          data-qa="remoteSshTempUser"
          label={t("Create temporary username and password")}
          required={false}
          disabled={!permissions.has("edgehost.sshUserUpdate")}
          helperText={
            <>
              <Trans>
                Enable the creation of a temporary user on the edge host with
                sudo privileges for SSH access from Palette. These credentials
                will be embedded in the SSH connection string for auto login,
                and the temporary user is deleted upon deactivation.
              </Trans>
              <div>
                <DocLink
                  location="remote-shell"
                  text={t("Learn more")}
                  fontSize={12}
                />
              </div>
            </>
          }
        />
      );
    }

    function renderRemoteShellToggles() {
      if (!isRemoteShellAllowed(agentVersion)) {
        return null;
      }

      return (
        <StyledWrapper>
          <StyledAlert
            description={t(
              "Remote shell will be disabled after 24h of inactivity"
            )}
            type="info"
            showIcon
          />
          <>
            <Fields.Switch
              name="remoteSsh"
              data-qa="remoteSsh"
              label={t("Remote shell")}
              required={false}
              disabled={
                !permissions.has("edgehost.sshUpdate") || !canUpdateRemoteShell
              }
              onChange={(value) => onRemoteShellToggle(value)}
              helperText={
                <>
                  <Trans>
                    Activate remote shell access to troubleshoot edge hosts by
                    initiating an SSH connection from Palette using the
                    configured username and password credentials.
                  </Trans>
                  <div>
                    <DocLink
                      location="remote-shell"
                      text={t("Learn more")}
                      fontSize={12}
                    />
                  </div>
                </>
              }
              {...(!canUpdateRemoteShell && {
                description: t(
                  "Remote Shell was disabled in user-data during deployment and can't be enabled from the UI."
                ),
              })}
            />
            {renderTemporaryAuthSwitch()}
          </>
        </StyledWrapper>
      );
    }

    return (
      <>
        <Fields.Tags name="tags" data-qa="tags" label={t("Tags")} />
        {renderRemoteShellToggles()}
      </>
    );
  }),
});

export default function EditPairingKeyConfirmationModal() {
  const { t } = useTranslation();
  return (
    <ConfirmationModal
      title={t("Edit Edge Host")}
      service={editHostPairingKeyModal}
    >
      <EditPairingKeyForm module={EDIT_HOST_PAIRING_KEY} />
    </ConfirmationModal>
  );
}
